import { useT } from "@transifex/react";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AreaButton, ButtonColorVariant, ButtonVariant } from "../../complex/teacherArea/AreaButton";
import type { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { useRTContext } from "../../context/RTContext";
import { useLogger } from "../../hooks/useLogger";
import useSchoolClassPage from "../../hooks/useSchoolClassPage";
import useToast from "../../hooks/useToast";
import SchoolClassCard from "../../complex/schoolClasses/SchoolClassCard";
import ToastMessage from "../../basic/toastMessage/ToastMessage";
import CreateClassPopup from "../../complex/schoolClasses/popups/CreateClassPopup";
import SchoolClassPageLayout from "./layout/SchoolClassPageLayout";
import { getClassDetailsRoute, getSchoolClassesRoute } from "../../helpers/navigationHelper";

const SchoolClasses = () => {
    const history = useHistory();
    const location = useLocation<{ showDeleteToast?: boolean }>();
    const t = useT();
    const { schoolClasses, createSchoolClass } = useRTContext();
    const { logEvent } = useLogger();

    const { isLoaded } = useSchoolClassPage(getSchoolClassesRoute());

    const t_backToAssistant = t("Back to the teacher assistant", {
        _tags: "SchoolClasses",
    });
    const t_myClasses = t("My Classes", {
        _tags: "SchoolClasses",
    });
    const t_newClass = t("New Class", {
        _tags: "SchoolClasses",
    });
    const t_emptyStateTitle = t("No classes available yet.", {
        _tags: "SchoolClasses",
    });
    const t_emptyStateDescription = t(
        "Create your first one now and easily share activities like vocabulary trainings, exercises, and tests with your class.",
        { _tags: "SchoolClasses" }
    );
    const t_deleteSuccess = t("Class successfully deleted!", {
        _tags: "SchoolClasses",
    });

    const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
    const deleteToast = useToast();
    const triggerDeleteToast = deleteToast.triggerToast;

    useEffect(() => {
        if (location.state?.showDeleteToast) {
            triggerDeleteToast();
            history.replace(getSchoolClassesRoute(), {});
        }
    }, [location.state, history, triggerDeleteToast]);

    const handleOpenCreatePopup = () => {
        logEvent("started class creation", { source: "classes_overview" });
        setIsCreatePopupOpen(true);
    };

    const handleCreateSchoolClass = async (schoolClassName: string, classVisibility: SchoolClassVisibilities) => {
        const result = await createSchoolClass(schoolClassName, classVisibility);
        return !!result;
    };

    return (
        <>
            <SchoolClassPageLayout
                backLabel={t_backToAssistant}
                onBack={() => history.push("/assistent")}
                data-testid="school-classes-screen"
            >
                <h1 className="text-xxl font-semibold text-p6-grey-dark text-center">{t_myClasses}</h1>

                <div className="self-end">
                    <AreaButton
                        data-testid="button-create-class"
                        variant={ButtonVariant.PRIMARY}
                        colorVariant={ButtonColorVariant.DEFAULT}
                        role="button"
                        onClick={handleOpenCreatePopup}
                        iconName="add"
                    >
                        {t_newClass}
                    </AreaButton>
                </div>

                {isLoaded && schoolClasses.length === 0 ? (
                    <div className="flex flex-col flex-1 gap-m items-center text-center max-w-[600px] self-center">
                        <p className="text-l text-p6-grey-dark">{t_emptyStateTitle}</p>
                        <p className="text-l text-p6-grey-dark">{t_emptyStateDescription}</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-s w-full">
                        {schoolClasses.map((classItem) => (
                            <SchoolClassCard
                                key={classItem.token}
                                classItem={classItem}
                                onClick={() => history.push(getClassDetailsRoute(classItem.token))}
                            />
                        ))}
                    </div>
                )}
            </SchoolClassPageLayout>

            <CreateClassPopup
                isOpen={isCreatePopupOpen}
                onClose={() => setIsCreatePopupOpen(false)}
                onSubmit={handleCreateSchoolClass}
                existingClassNames={schoolClasses.map((c) => c.name)}
            />

            <ToastMessage
                text={t_deleteSuccess}
                isOpen={deleteToast.isOpen}
                onOpenChange={deleteToast.onOpenChange}
            />
        </>
    );
};

export default SchoolClasses;
